import { resolveDateLocale, useI18n } from '../../../i18n';
import { DataTable, Column } from '../../../components/shared/DataTable';
import { Pagination } from '../../../components/shared/Pagination';
import { MoreVerticalIcon } from '../../../components/icons';
import { AvatarInitials } from '../../customers/components/AvatarInitials';
import { JobTitleBadge } from './JobTitleBadge';
import { EmployeeStatusDot, type EmployeeStatus } from './EmployeeStatusDot';

// ============================================
// Types
// ============================================

export interface EmployeeDisplay {
  id: string;
  code: string;
  fullName: string;
  phone?: string;
  jobTitle: string;
  status: EmployeeStatus;
  startDate: string;
}

interface EmployeeTableProps {
  data: EmployeeDisplay[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRowAction: (employeeId: string, action: string) => void;
  isLoading?: boolean;
}

// ============================================
// Helpers
// ============================================

function formatDate(dateStr: string, locale: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}

// ============================================
// Component
// ============================================

export function EmployeeTable({
  data,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onRowAction,
  isLoading,
}: EmployeeTableProps) {
  const { t, language } = useI18n();
  const dateLocale = resolveDateLocale(language, 'en-US');
  const totalPages = Math.ceil(totalCount / pageSize);

  const columns: Column<EmployeeDisplay>[] = [
    {
      key: 'code',
      header: t('code'),
      width: '90px',
      render: (row) => (
        <span className="font-mono text-xs font-bold text-primary-600">
          {row.code}
        </span>
      ),
    },
    {
      key: 'name',
      header: t('name'),
      width: '180px',
      render: (row) => (
        <div className="flex items-center gap-3">
          <AvatarInitials name={row.fullName} colorSeed={row.id} size="sm" />
          <span className="font-semibold text-slate-900">{row.fullName}</span>
        </div>
      ),
    },
    {
      key: 'phone',
      header: t('phone'),
      width: '140px',
      render: (row) => (
        <span className="text-sm text-[#434655]">{row.phone || '-'}</span>
      ),
    },
    {
      key: 'jobTitle',
      header: t('enterPositionRole').replace('Enter ', '').replace('role', 'Title'),
      width: '150px',
      render: (row) => {
        const roleKeys: Record<string, string> = {
          STORE_ADMIN: 'roleStoreAdmin',
          OPERATOR: 'roleOperator',
          CUSTOMER: 'roleCustomer',
          AI_ADMIN: 'roleAiAdmin',
          TECHNICIAN: 'roleTechnician',
          POS_TERMINAL: 'rolePosTerminal',
        };
        const key = roleKeys[row.jobTitle];
        return <JobTitleBadge>{key ? t(key) : row.jobTitle}</JobTitleBadge>;
      },
    },
    {
      key: 'status',
      header: t('status'),
      width: '110px',
      render: (row) => <EmployeeStatusDot status={row.status} />,
    },
    {
      key: 'startDate',
      header: t('creationDate').replace('Creation', 'Start'),
      width: '100px',
      render: (row) => (
        <span className="text-sm text-[#434655]">{formatDate(row.startDate, dateLocale)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      align: 'right',
      render: (row) => {
        const isActive = row.status === 'active';
        return (
          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onRowAction(row.id, 'edit')}
              className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title={t('editEmployee')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
              </svg>
            </button>
            {/* Đặt lại mật khẩu (sheet 2 #63) — server sinh mật khẩu và trả
                về đúng một lần, trang cha hiện trong hộp thoại để chép lại. */}
            <button
              onClick={() => onRowAction(row.id, 'reset-password')}
              className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-amber-600"
              title={t('resetPassword')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </button>
            <button
              onClick={() => onRowAction(row.id, isActive ? 'deactivate' : 'activate')}
              className={`flex h-7 w-7 items-center justify-center rounded transition-colors ${
                isActive 
                  ? 'text-red-500 hover:bg-red-50 hover:text-red-600' 
                  : 'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700'
              }`}
              title={isActive ? t('deactivateEmployee') : t('activateEmployee')}
            >
              {isActive ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                </svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-sm">
      <DataTable
        columns={columns}
        data={data}
        rowKey="id"
        isLoading={isLoading}
        emptyMessage="No employees found"
        showFilter={false}
        showHeaderMenu={false}
        className="border-0 shadow-none"
      />

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="border-t border-slate-100">
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
            itemLabel="employees"
          />
        </div>
      )}
    </div>
  );
}
